import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { BottomBar, Wrapper } from "~/components/preview-components";
import {
  SteppedPreview,
  useCurrentStep,
  Text,
} from "~/components/stepped-preview";

export const AbsolutePositioning = () => {
  const [isAbsolute, setIsAbsolute] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [isPadding, setIsPadding] = useState(false);

  return (
    <Wrapper>
      <div className="grid place-items-center">
        <div
          className={twMerge(
            "relative flex h-8 border-2 border-[red]",
            isPadding && "pr-12",
          )}
        >
          <input
            type="text"
            className="min-w-0 rounded-l-full border border-white bg-white"
          />
          <button
            className={twMerge(
              "h-full shrink-0 rounded-r-full bg-black px-3 py-1",
              isAbsolute && "absolute",
              isRight && "right-0",
              isPadding && "w-12",
            )}
          >
            Click me
          </button>
        </div>
      </div>
      <BottomBar>
        <div className="flex justify-center gap-2 max-md:col-span-2 max-md:flex-col md:col-start-2">
          <button
            onClick={() => setIsAbsolute((s) => !s)}
            className="flex items-center justify-center gap-2 rounded-lg border border-white-opaque bg-white-opaque px-3 py-1 text-md transition-colors duration-300 hover:bg-background-contrast focus-visible:bg-background-contrast"
          >
            Make button position: {isAbsolute ? "relative" : "absolute"}
          </button>
          <button
            onClick={() => setIsRight((s) => !s)}
            className="flex items-center justify-center gap-2 rounded-lg border border-white-opaque bg-white-opaque px-3 py-1 text-md transition-colors duration-300 hover:bg-background-contrast focus-visible:bg-background-contrast"
          >
            {isRight ? "Remove" : "Give"} button right: 0px
          </button>
          <button
            onClick={() => setIsPadding((s) => !s)}
            className="flex items-center justify-center gap-2 rounded-lg border border-white-opaque bg-white-opaque px-3 py-1 text-md transition-colors duration-300 hover:bg-background-contrast focus-visible:bg-background-contrast"
          >
            {isPadding ? "Remove padding from parent" : "Add padding to parent"}
          </button>
        </div>
      </BottomBar>
    </Wrapper>
  );
};

export const TwoColGrid = ({
  maxWidth = "auto",
}: {
  maxWidth?: "auto" | "fixed";
}) => {
  const [text, setText] = useState("2");
  return (
    <Wrapper>
      <div
        className={twMerge(
          "grid w-full *:grid *:h-12 *:place-items-center *:rounded-2xl *:border *:bg-white-opaque *:text-center",
          maxWidth === "auto"
            ? "grid-cols-[1fr_auto]"
            : "grid-cols-[1fr_120px]",
        )}
      >
        <div>1</div>
        <div>{text}</div>
      </div>
      <BottomBar>
        <div className="col-start-2 flex items-center justify-center gap-2 max-md:flex-col">
          <p className="max-md:text-center">
            Change the text in the second column:
          </p>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-48 rounded-lg border border-white-opaque bg-white-opaque px-3 py-1"
          />
        </div>
      </BottomBar>
    </Wrapper>
  );
};

export const OverlappingTwoColGrid = () => {
  return (
    <SteppedPreview
      steps={[
        { label: "Start", duration: 9000 },
        { label: "grid-column", duration: 14000 },
        { label: "Two lines?", duration: 12500 },
        { label: "Move to row 1", duration: 10000 },
        { label: "Back to one line", duration: 18000 },
        { label: "Assign column", duration: 11000 },
      ]}
    >
      <OverlappingTwoColGridContent />
    </SteppedPreview>
  );
};

const lines = [
  { line: 7, step: 4 },
  { line: 8, step: 5 },
  { line: 12, step: 3 },
  { line: 13, step: 2 },
];

export const OverlappingTwoColGridContent = () => {
  const step = useCurrentStep();
  const currentStep = step ?? 0;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    lines.forEach((line) => {
      if (!ref.current) return;
      const lineEl = ref.current
        .closest(".preview-with-code")
        ?.querySelector(
          `.expressive-code .ec-line:nth-of-type(${line.line}) .code`,
        );
      if (!lineEl) return;

      lineEl.classList.add("transition-all");
      if (currentStep < line.step) {
        lineEl.classList.add("line-through", "opacity-50");
      } else {
        lineEl.classList.remove("line-through", "opacity-50");
      }
    });
  }, [currentStep]);

  return (
    <AnimatePresence>
      <motion.div layout ref={ref} className="w-full">
        <div className="h-12">
          <motion.div
            layout
            className={twMerge(
              "group grid w-full grid-cols-[1fr_120px] *:grid *:h-12 *:place-items-center *:rounded-2xl *:border *:bg-white-opaque *:p-4 *:text-center",
            )}
          >
            <motion.div
              layout
              className={twMerge(
                currentStep >= 4 && "row-start-1",
                currentStep === 3 && "justify-items-start!",
                currentStep >= 5 && "col-start-1",
              )}
            >
              1
            </motion.div>
            <motion.div
              layout
              className={twMerge(
                "relative transition-[margin] duration-300",
                currentStep >= 2 && "col-span-2 col-start-1",
                currentStep >= 3 && "row-start-1",
                currentStep === 2 && "justify-items-start!",
                currentStep === 5 && "group-hover:mt-5",
              )}
            >
              2
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          layout
          className="relative z-10 mx-auto grid w-full max-w-[600px] items-start pt-3 *:[grid-area:1/1]"
        >
          <Text isActive={step === 0}>
            Overlapping elements with CSS Grid takes a few steps. Let's talk
            about them. (The code below is updating with every step too)
          </Text>
          <Text isActive={step === 1}>
            First we add the <code>grid-column</code> property to our second
            div.
            <br />
            By specifying this as <code>grid-column: 1 / span 2;</code>, we're
            telling the browser to put this div in the first column, and make it
            span two columns. Let's see what happens 👉
          </Text>

          <Text isActive={step === 2}>
            The second div now spans the width of both columns. It did however
            also move to the second line. <br />
            As soon as elements start overlapping columns in Grid, CSS will move
            these elements to a new row.
          </Text>

          <Text isActive={step === 3}>
            We can prevent this from happening, by adding{" "}
            <code>grid-row: 1;</code> to the second div. But now, the first div
            moves to the second row..
          </Text>

          <Text isActive={step === 4}>
            You guessed it. Adding <code>grid-row: 1;</code> to the first div as
            well, will put them both on the same row. Yet they still don't
            overlap. Right now the Grid introduced a third column, where this
            first div is placed. This is because we don't specify a specific
            column for this div to be in, and all columns are currently "taken".
          </Text>
          <Text isActive={step === 5}>
            The final step to make them overlap here, is to put the first div
            back in the first column by using <code>grid-column: 1;</code>.
            <br />
            Hover the two columns to shift them slightly to see the overlap.
          </Text>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
